import React, { useState, useEffect } from 'react';
import settingsService, { Settings } from '../services/settings.service';
import { GeneralSettings } from '../components/settings/GeneralSettings';
import { SystemSettings } from '../components/settings/SystemSettings';
import { UsersSettings } from '../components/settings/UsersSettings';

type SettingsTab = 'general' | 'system' | 'users';

export const SettingsPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<SettingsTab>('general');
  const [settings, setSettings] = useState<Settings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [checkingHealth, setCheckingHealth] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [syncErrors, setSyncErrors] = useState<{ category: string; errors: string[] }[] | null>(null);
  const [connectionStatus, setConnectionStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [lastSync, setLastSync] = useState<any>(null);
  const [dbHealth, setDbHealth] = useState<any>(null);

  // Form state
  const [spreadsheetId, setSpreadsheetId] = useState('');
  const [syncFrequency, setSyncFrequency] = useState('15');
  const [autoSync, setAutoSync] = useState(true);
  const [useWeightedValues, setUseWeightedValues] = useState(true);

  // Load settings on mount
  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      setSyncErrors(null);
      const response = await settingsService.getSettings();

      if (response.success) {
        setSettings(response.data.settings);
        setLastSync(response.data.lastSync);

        // Populate form fields
        if (response.data.settings.google_sheets_spreadsheet_id) {
          setSpreadsheetId(response.data.settings.google_sheets_spreadsheet_id.value);
        }
        if (response.data.settings.sync_frequency_minutes) {
          setSyncFrequency(response.data.settings.sync_frequency_minutes.value);
        }
        if (response.data.settings.google_sheets_auto_sync) {
          setAutoSync(response.data.settings.google_sheets_auto_sync.value === 'true');
        }
        if (response.data.settings.use_weighted_values) {
          setUseWeightedValues(response.data.settings.use_weighted_values.value === 'true');
        }
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleTestConnection = async () => {
    if (!spreadsheetId.trim()) {
      setError('Please enter a spreadsheet ID');
      return;
    }

    try {
      setTesting(true);
      setError(null);
      setSuccess(null);
      setSyncErrors(null);
      setConnectionStatus('idle');

      const response = await settingsService.testConnection(spreadsheetId);

      if (response.success) {
        setConnectionStatus('success');
        setSuccess(`Connected to "${response.data?.title}" - ${response.data?.sheetCount} sheets found`);
      } else {
        setConnectionStatus('error');
        setError(response.error || 'Connection failed');
      }
    } catch (err: any) {
      setConnectionStatus('error');
      setError(err.response?.data?.error || err.message || 'Failed to test connection');
    } finally {
      setTesting(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      setError(null);
      setSuccess(null);
      setSyncErrors(null);

      const updates: Record<string, string> = {
        google_sheets_spreadsheet_id: spreadsheetId,
        sync_frequency_minutes: syncFrequency,
        google_sheets_auto_sync: autoSync ? 'true' : 'false',
        use_weighted_values: useWeightedValues ? 'true' : 'false',
      };

      const response = await settingsService.updateSettings(updates);

      if (response.success) {
        setSuccess('Settings saved successfully');
        setConnectionStatus('idle');
        // Reload settings to get updated values
        await loadSettings();
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleSyncNow = async () => {
    try {
      setSyncing(true);
      setError(null);
      setSuccess(null);
      setSyncErrors(null);

      const response = await settingsService.syncNow();

      if (response.success) {
        setSuccess(`Sync triggered successfully! Synced ${response.data?.totalRecords || 0} records. Refreshing in 5 seconds...`);
        setSyncErrors(null);
        // Reload settings to get updated sync status
        setTimeout(() => {
          loadSettings();
          setSuccess(`Sync completed! Processed ${response.data?.totalRecords || 0} records.`);
        }, 5000);
      } else {
        // Extract detailed errors from sync results
        const results = response.data?.results;
        if (results) {
          const errorDetails: { category: string; errors: string[] }[] = [];

          const categoryNames: Record<string, string> = {
            salesReps: 'Sales Reps',
            deals: 'Deals',
            territories: 'Territories',
            costSavings: 'Cost Savings',
            acquisitions: 'Acquisitions',
            existingBase: 'Existing Base',
            leads: 'Leads',
            commentary: 'Commentary',
            riskItems: 'Risk Items',
            mitigationItems: 'Mitigation Items',
          };

          Object.entries(results).forEach(([key, value]: [string, any]) => {
            if (value?.errors && value.errors.length > 0) {
              errorDetails.push({
                category: categoryNames[key] || key,
                errors: value.errors,
              });
            }
          });

          setSyncErrors(errorDetails);
        }
        setError(`Sync completed with ${response.data?.totalErrors || 0} errors. See details below:`);
      }
    } catch (err: any) {
      const errorMsg = err.response?.data?.error || err.response?.data?.details || err.message || 'Failed to trigger sync';
      setError(`Sync failed: ${errorMsg}`);
      setSyncErrors(null);
    } finally {
      setSyncing(false);
    }
  };

  const handleCheckDatabaseHealth = async () => {
    try {
      setCheckingHealth(true);
      setError(null);
      setSuccess(null);

      const response = await settingsService.checkDatabaseHealth();

      if (response.success) {
        setDbHealth(response.data);
        if (response.data.status === 'healthy') {
          setSuccess('Database is healthy');
        } else if (response.data.status === 'warning') {
          setError(`Database has warnings: ${response.data.issues.join(', ')}`);
        } else {
          setError(`Database has errors: ${response.data.issues.join(', ')}`);
        }
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Failed to check database health');
      setDbHealth(null);
    } finally {
      setCheckingHealth(false);
    }
  };

  const tabs = [
    { id: 'general' as SettingsTab, label: 'General', icon: '⚙️' },
    { id: 'system' as SettingsTab, label: 'System', icon: '🗄️' },
    { id: 'users' as SettingsTab, label: 'Users', icon: '👤' },
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="bg-black text-white p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-3xl font-bold mb-2">Settings</h1>
          <p className="text-gray-400">Configure your dashboard settings and manage users</p>
        </div>

        {/* Error Message */}
        {error && (
          <div className="mb-6 bg-red-900/30 border border-red-500 text-red-200 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Sync Error Details */}
        {syncErrors && syncErrors.length > 0 && (
          <div className="mb-6 bg-gray-900 border border-gray-700 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-800 border-b border-gray-700">
              <h3 className="text-white font-semibold">Sync Error Details</h3>
              <p className="text-gray-400 text-sm">Click on a category to see individual errors</p>
            </div>
            <div className="divide-y divide-gray-700">
              {syncErrors.map((category, idx) => (
                <details key={idx} className="group">
                  <summary className="px-4 py-3 cursor-pointer hover:bg-gray-800 flex justify-between items-center">
                    <span className="text-orange-400 font-medium">{category.category}</span>
                    <span className="text-red-400 text-sm">{category.errors.length} error(s)</span>
                  </summary>
                  <div className="px-4 py-3 bg-gray-950 max-h-60 overflow-y-auto">
                    <ul className="space-y-2 text-sm">
                      {category.errors.map((err, errIdx) => (
                        <li key={errIdx} className="text-red-300 flex items-start">
                          <span className="text-red-500 mr-2">•</span>
                          <span className="break-all">{err}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </details>
              ))}
            </div>
          </div>
        )}

        {/* Success Message */}
        {success && (
          <div className="mb-6 bg-green-900/30 border border-green-500 text-green-200 px-4 py-3 rounded">
            {success}
          </div>
        )}

        {/* Main Content with Vertical Sub-Menu */}
        <div className="flex gap-6">
          {/* Left Sidebar - Vertical Sub-Menu */}
          <div className="w-48 flex-shrink-0">
            <nav className="space-y-1">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`w-full flex items-center px-4 py-3 text-sm font-medium rounded-lg transition-colors ${
                    activeTab === tab.id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  <span className="mr-3">{tab.icon}</span>
                  {tab.label}
                </button>
              ))}
            </nav>

            {/* Save Button - Only show for General tab */}
            {activeTab === 'general' && (
              <div className="mt-6">
                <button
                  onClick={handleSaveSettings}
                  disabled={saving}
                  className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-700 disabled:cursor-not-allowed rounded-lg font-semibold transition"
                >
                  {saving ? 'Saving...' : 'Save Settings'}
                </button>
              </div>
            )}
          </div>

          {/* Right Content Area */}
          <div className="flex-1 min-w-0">
            {activeTab === 'general' && (
              <GeneralSettings
                spreadsheetId={spreadsheetId}
                setSpreadsheetId={setSpreadsheetId}
                syncFrequency={syncFrequency}
                setSyncFrequency={setSyncFrequency}
                autoSync={autoSync}
                setAutoSync={setAutoSync}
                useWeightedValues={useWeightedValues}
                setUseWeightedValues={setUseWeightedValues}
                testing={testing}
                syncing={syncing}
                connectionStatus={connectionStatus}
                lastSync={lastSync}
                onTestConnection={handleTestConnection}
                onSyncNow={handleSyncNow}
              />
            )}

            {activeTab === 'system' && (
              <SystemSettings
                settings={settings}
                dbHealth={dbHealth}
                checkingHealth={checkingHealth}
                onCheckDatabaseHealth={handleCheckDatabaseHealth}
              />
            )}

            {activeTab === 'users' && <UsersSettings />}
          </div>
        </div>
      </div>
    </div>
  );
};
